// API Configuration
import { Sentry } from '../sentry'
import { captureApiMutation } from '../posthog'

const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:3000/api';

export interface ApiResponse<T> {
  data: T;
  error?: string;
  message?: string;
}

export interface ApiError {
  message: string;
  status: number;
  details?: unknown;
  timestamp?: string;
  errorRef?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get JWT token from localStorage
    const token = localStorage.getItem('auth_token');
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options?.headers,
        },
      });

      if (!response.ok) {
        let error: ApiError = {
          message: response.statusText,
          status: response.status,
        };
        
        try {
          const errorData = await response.json();
          // Handle backend error structure
          error = {
            message: errorData.message || response.statusText,
            status: errorData.status || response.status,
            timestamp: errorData.timestamp,
            errorRef: errorData.errorRef,
            details: errorData,
          };
        } catch {
          // Response is not JSON
        }

        if (error.status >= 500) {
          Sentry.captureException(new Error(error.message || 'API server error'), {
            tags: {
              api_status: String(error.status),
              endpoint,
            },
            extra: {
              errorRef: error.errorRef,
              timestamp: error.timestamp,
            },
          });
        }
        
        throw error;
      }

      // 204/205 and empty bodies mean "no resource" — never coerce to {}.
      if (response.status === 204 || response.status === 205) {
        captureApiMutation(options?.method || 'GET', endpoint)
        return null as T;
      }

      const text = await response.text();
      if (!text) {
        captureApiMutation(options?.method || 'GET', endpoint)
        return null as T;
      }
      const data = JSON.parse(text) as T
      captureApiMutation(options?.method || 'GET', endpoint)
      return data
    } catch (error) {
      if ((error as ApiError).status) {
        throw error
      }
      
      throw {
        message: 'Network error or server unavailable',
        status: 0,
        details: error,
      } as ApiError;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
